// src/components/Player/FloatingPlayer.js
// Literal markup match of the reference's .a-player/.disc/.a-ptext/
// .a-ptitle/.a-psub/.a-transport/.a-tbtn/.a-tbtn-play/.a-wave/.a-hide-btn/
// .a-show-tab, wired to the real PlayerContext (togglePlayPause,
// skipBackward/skipForward, formatTime, togglePlayerMinimized).
//
// The seek bar (.a-progress) is a real, functional addition beyond the
// literal reference — Sean asked for it back after the previous pass
// dropped it to match the static mockup exactly. It's a native
// <input type="range"> (free dragging + click-to-seek + keyboard support,
// same as the seekTo(percentage) pattern the old progress-bar div used)
// wired to the same real `progress`/`seekTo` state PlayerContext already
// exposes — nothing fake or static. Volume control stays out of scope
// (not requested this round).
import React from 'react';
import { usePlayer } from '../../contexts/PlayerContext';
import { resolveImageUrl } from '../../utils/imageUrl';
import './FloatingPlayer.css';

const FloatingPlayer = () => {
  const {
    isFloatingPlayerVisible,
    isPlayerMinimized,
    currentEpisode,
    isPlaying,
    progress,
    currentTime,
    duration,
    togglePlayPause,
    skipBackward,
    skipForward,
    seekTo,
    formatTime,
    togglePlayerMinimized,
  } = usePlayer();

  if (!isFloatingPlayerVisible || !currentEpisode) {
    return null;
  }

  const progressValue = Number.isFinite(progress) ? progress : 0;

  const handleSeekChange = (e) => {
    seekTo(Number(e.target.value));
  };

  return (
    <>
      <div className={`a-player ${isPlaying ? '' : 'is-paused'} ${isPlayerMinimized ? 'is-hidden' : ''}`}>
        <div
          className="disc"
          aria-hidden="true"
          style={currentEpisode.thumbnailUrl ? { backgroundImage: `url(${resolveImageUrl(currentEpisode.thumbnailUrl)})` } : undefined}
        />
        <div className="a-ptext">
          <div className="a-ptitle">{currentEpisode.title}</div>
          <div className="a-psub">{formatTime(currentTime)} / {formatTime(duration)}</div>
        </div>
        <div className="a-progress">
          <span className="a-progress-time" aria-hidden="true">{formatTime(currentTime)}</span>
          <input
            className="a-progress-range"
            type="range"
            min="0"
            max="100"
            step="0.1"
            value={progressValue}
            onChange={handleSeekChange}
            style={{ '--fill': `${progressValue}%` }}
            aria-label="Seek episode position"
            aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
          />
          <span className="a-progress-time" aria-hidden="true">{formatTime(duration)}</span>
        </div>
        <div className="a-transport">
          <button className="a-tbtn" type="button" onClick={() => skipBackward(10)} title="Back 10 seconds">
            <svg fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="16">
              <path d="M4 12a8 8 0 1 0 2.34-5.66" />
              <polyline points="4 4 4 9 9 9" />
            </svg>
            <span className="secs">10</span>
          </button>
          <button
            className={`a-tbtn a-tbtn-play ${isPlaying ? 'is-playing' : ''}`}
            type="button"
            onClick={togglePlayPause}
            title="Play / Pause"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            <svg className="icon-play" height="16" viewBox="0 0 24 24" width="16">
              <polygon fill="currentColor" points="6,4 20,12 6,20" />
            </svg>
            <svg className="icon-pause" height="16" viewBox="0 0 24 24" width="16">
              <rect fill="currentColor" height="16" width="5" x="5" y="4" />
              <rect fill="currentColor" height="16" width="5" x="14" y="4" />
            </svg>
          </button>
          <button className="a-tbtn" type="button" onClick={() => skipForward(10)} title="Forward 10 seconds">
            <svg fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="16">
              <path d="M20 12a8 8 0 1 1-2.34-5.66" />
              <polyline points="20 4 20 9 15 9" />
            </svg>
            <span className="secs">10</span>
          </button>
        </div>
        <div className="a-wave" aria-hidden="true">
          {Array.from({ length: 12 }).map((_, i) => <i key={i} />)}
        </div>
        <button className="a-hide-btn" type="button" onClick={togglePlayerMinimized} title="Hide player">
          <svg fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="14">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </div>
      <button
        className={`a-show-tab ${isPlayerMinimized ? 'is-visible' : ''}`}
        type="button"
        onClick={togglePlayerMinimized}
        title="Show player"
      >
        ▲ Show Player
      </button>
    </>
  );
};

export default FloatingPlayer;
